import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  AttachMoney as MoneyIcon,
  LocationCity as LocationIcon,
  TrendingUp as TrendingIcon,
  SupportAgent as SupportIcon,
  CrisisAlert as AlertIcon,
} from '@mui/icons-material';

import SearchBar from '../components/search/SearchBar';
import PropertyCarousel from '../components/property/PropertyCarousel';
import { PropertyType } from '../components/property/PropertyCard';
import { mockProperties, mockFeaturedProperties } from '../utils/mockData';

const HomePage = () => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [properties, setProperties] = useState<PropertyType[]>([]);
  const [featuredProperties, setFeaturedProperties] = useState<PropertyType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate API call
    const fetchProperties = async () => {
      setIsLoading(true);
      try {
        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setProperties(mockProperties);
        setFeaturedProperties(mockFeaturedProperties);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const heroImageUrl = 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';

  const features = [
    {
      icon: <SearchIcon color="primary" fontSize="large" />,
      title: 'Easy Property Search',
      description: 'Find your ideal property with our powerful search tools',
    },
    {
      icon: <MoneyIcon color="primary" fontSize="large" />,
      title: 'Best Price Guarantee',
      description: 'We ensure competitive pricing for all our property listings',
    },
    {
      icon: <LocationIcon color="primary" fontSize="large" />,
      title: 'Prime Locations',
      description: 'Access to exclusive properties in the most desirable areas',
    },
    {
      icon: <SupportIcon color="primary" fontSize="large" />,
      title: 'Expert Agents',
      description: 'Our experienced agents will guide you through every step',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '70vh', md: '85vh' },
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          overflow: 'hidden',
        }}
      >
        {/* Hero Background Image with Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
            },
          }}
        >
          <Box
            component="img"
            src={heroImageUrl}
            alt="Luxury Home"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>

        {/* Hero Content */}
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
            pt: { xs: 4, md: 0 },
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            fontWeight={800}
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            Find Your Dream Home
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: 4,
              maxWidth: 700,
              mx: 'auto',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            Discover thousands of properties for sale and rent across the country
          </Typography>

          {/* Search Bar */}
          <SearchBar variant="hero" />

          {/* Quick Stat Cards */}
          <Grid
            container
            spacing={2}
            justifyContent="center"
            sx={{ mt: 6, display: { xs: 'none', md: 'flex' } }}
          >
            {[
              { label: 'Properties Listed', value: '12,500+', icon: <HomeIcon /> },
              { label: 'Happy Clients', value: '9,840+', icon: <TrendingIcon /> },
              { label: 'Cities Covered', value: '380+', icon: <LocationIcon /> },
              { label: 'Experienced Agents', value: '350+', icon: <SupportIcon /> },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    py: 2,
                    px: 3,
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 0, textAlign: 'center' }}>
                    <Box sx={{ color: 'primary.main', mb: 1 }}>{stat.icon}</Box>
                    <Typography variant="h4" component="div" fontWeight={700} color="text.primary">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Feature Cards Section */}
      <Container sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            component="h2"
            fontWeight={700}
            gutterBottom
            color="primary"
          >
            Why Choose Us
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Discover the key features that make DwellHub the best place to find your next home
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                  p: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  },
                }}
                elevation={2}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Featured Properties Section */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900', py: 8 }}>
        <Container>
          <PropertyCarousel
            title="Featured Properties"
            description="Explore our handpicked selection of premium properties"
            properties={featuredProperties}
            viewAllLink="/listings?featured=true"
            variant="featured"
          />
        </Container>
      </Box>

      {/* Recently Added Properties */}
      <Container sx={{ py: 8 }}>
        <PropertyCarousel
          title="Recently Added"
          description="Check out the newest properties on the market"
          properties={properties.filter(p => p.isNew)}
          viewAllLink="/listings?sort=newest"
        />
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          py: { xs: 6, md: 10 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            left: -20,
            width: 200,
            height: 200,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            right: -50,
            width: 250,
            height: 250,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" component="h2" fontWeight={700} gutterBottom>
                Ready to Find Your Perfect Home?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Join thousands of satisfied customers who found their dream property with us
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
              >
                <Button
                  variant="contained"
                  size="large"
                  color="secondary"
                  href="/listings"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  Browse Properties
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Contact an Agent
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 300,
                  width: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 15px 30px rgba(0,0,0,0.2)',
                }}
              >
                <CardMedia
                  component="img"
                  height="300"
                  image="https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Happy family in new home"
                  sx={{
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            component="h2"
            fontWeight={700}
            gutterBottom
          >
            What Our Clients Say
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Hear from our satisfied customers who found their perfect homes
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {[
            {
              name: 'Sarah Johnson',
              location: 'New York, NY',
              image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=600',
              testimonial: 'DwellHub made finding our family home so easy! The search tools were intuitive, and our agent was incredibly helpful throughout the entire process.',
            },
            {
              name: 'Michael Rodriguez',
              location: 'Los Angeles, CA',
              image: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=600',
              testimonial: 'As a first-time homebuyer, I was nervous about the process, but the DwellHub team guided me every step of the way. I found a beautiful condo that was within my budget!',
            },
            {
              name: 'Emily Chen',
              location: 'Seattle, WA',
              image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=600',
              testimonial: 'The virtual tours saved me so much time during my property search. I was relocating from another state, and DwellHub made the process seamless.',
            },
          ].map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 3,
                  borderRadius: 3,
                }}
                elevation={2}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ 
                      fontStyle: 'italic', 
                      position: 'relative',
                      '&::before': {
                        content: '"""',
                        fontSize: '4rem',
                        position: 'absolute',
                        top: -20,
                        left: -10,
                        color: theme.palette.grey[200],
                        zIndex: 0,
                      },
                    }}
                  >
                    {testimonial.testimonial}
                  </Typography>
                </CardContent>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
                  <Box
                    component="img"
                    src={testimonial.image}
                    alt={testimonial.name}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.location}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Alert Banner */}
      <Box sx={{ bgcolor: 'warning.main', py: 2, color: 'warning.contrastText' }}>
        <Container>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 1,
            }}
          >
            <AlertIcon />
            <Typography variant="body1" fontWeight={500}>
              COVID-19 Update: We're still open for business with enhanced safety measures. Virtual tours available!
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;